import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@core-ui/Components/ui/dropdown-menu.tsx';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@core-ui/Components/ui/avatar.tsx';
import { Camera, ChevronsUpDown, LogOut } from 'lucide-react';
import React from 'react';
import { getCurrentUser } from '@features/auth';
import { useSessionStore } from '@features/auth/store/useSessionStore.ts';
import { publicRoutes } from '@constants';
import { useTranslation } from 'react-i18next';
import { Input } from '@core-ui/Components/ui/input.tsx';
import { pauseActiveSdrSession } from '@features/wizard/utils/pauseSdrSession';

interface UserDropdownProps {
  isOpen: boolean;
}

const UserDropdown: React.FC<UserDropdownProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const currentUser = getCurrentUser();
  const { endSession } = useSessionStore();

  const handleLogout = async (): Promise<void> => {
    // Pause the SDR's running session first so connected agents lock their window.
    if (currentUser?.isSdr === true && currentUser.userId != null) {
      await pauseActiveSdrSession(currentUser.userId);
    }
    await endSession();
    window.location.href = `/workspace${publicRoutes.LOGIN}`;
  };

  if (currentUser == null) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          className={`w-full flex items-center rounded-lg px-2 py-1.5 transition-colors cursor-pointer 
          ${isOpen ? 'justify-start gap-3 hover:bg-muted/50' : 'justify-center'}`}
        >
          <Avatar className="h-9 w-9 rounded-lg bg-muted border border-border shadow-sm overflow-hidden">
            <AvatarImage
              src={currentUser.avatar}
              alt={currentUser.userName}
              className=" bg-white dark:bg-background"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <AvatarFallback className="rounded-lg text-foreground text-xs font-medium bg-white dark:bg-muted">
              {currentUser.userName?.[0] ?? 'U'}
            </AvatarFallback>
          </Avatar>

          {isOpen && (
            <>
              <div
                className="flex-1 overflow-hidden text-sm leading-tight"
              >
                <div className="font-semibold dark:text-foreground truncate">
                  {currentUser.userName}
                </div>
                <div className="text-xs dark:text-muted-foreground truncate">
                  {currentUser.email}
                </div>
              </div>
              <ChevronsUpDown className="ml-auto size-4 dark:text-muted-foreground" />
            </>
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="min-w-56 rounded-lg bg-white dark:bg-[oklch(0.12_0.01_237)] text-popover-foreground border border-border shadow-md"
        side={isOpen ? 'right' : 'bottom'}
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-3 py-2 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage
                src={currentUser.avatar}
                alt={currentUser.userName}
              />
              <AvatarFallback className="rounded-lg">U</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium text-foreground">
                {currentUser.userName}
              </span>
              <span className="truncate text-xs !text-[oklch(0.75_0.01_237)] dark:text-muted-foreground">
                {currentUser.email}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          {currentUser.isAgent && (
            <>
              <DropdownMenuItem
                onSelect={() => {
                  document.getElementById('profile-picture-input')?.click();
                }}
                className="hover-fix cursor-pointer flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors text-foreground dark:hover:bg-[var(--sidebar-hover-dark)] hover:text-white"
              >
                <Camera className="size-4 hover:text-white" />
                <span>{t('userDropdown.changeProfilePicture')}</span>
              </DropdownMenuItem>

              <Input
                type="file"
                id="profile-picture-input"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files;

                  console.log(file);
                  if (file != null) {
                    // TODO: handle file upload (preview, API integration, etc.)
                  }
                }}
              />
            </>
          )}
          <DropdownMenuItem
            onSelect={() => {
              void handleLogout();
            }}
            className={
              'hover-fix cursor-pointer flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors text-destructive dark:hover:bg-[var(--sidebar-hover-dark)]'
            }
          >
            <LogOut
              data-qa-id="log-out"
              className="size-4 hover:text-white dark:text-white"
            />
            <span className="dark:text-white">{t('userDropdown.logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdown;
